import React, { useState } from "react";
import { toast } from "sonner";
import { Mail, Loader2, Copy, Download } from "lucide-react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";

export default function CoverLetterDialog({ draft }) {
  const [open, setOpen] = useState(false);
  const [company, setCompany] = useState("");
  const [generating, setGenerating] = useState(false);
  const [letter, setLetter] = useState("");
  const [downloadingZip, setDownloadingZip] = useState(false);

  const generate = async () => {
    setGenerating(true);
    setLetter("");
    try {
      const { data } = await api.post("/resume-builder/cover-letter", { target_role: draft.target_role, company });
      setLetter(data.cover_letter);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not generate cover letter right now");
    } finally {
      setGenerating(false);
    }
  };

  const copyLetter = () => {
    navigator.clipboard.writeText(letter);
    toast.success("Cover letter copied");
  };

  const downloadZipBundle = async () => {
    setDownloadingZip(true);
    try {
      const response = await api.post("/resume-builder/bundle/zip", { cover_letter: letter }, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: "application/zip" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = `${(draft?.full_name || "Candidate").replace(/\s+/g, "_")}_Application_Bundle.zip`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Resume + Cover Letter bundle downloaded");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not generate the ZIP bundle right now");
    } finally {
      setDownloadingZip(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid="resume-cover-letter-open-button" className="flex items-center gap-1.5 text-xs font-semibold text-emerald-300 hover:text-emerald-200">
          <Mail className="w-3.5 h-3.5" /> Generate Cover Letter
        </button>
      </DialogTrigger>
      <DialogContent className="max-w-lg" data-testid="resume-cover-letter-dialog">
        <DialogHeader>
          <DialogTitle className="text-white">AI Cover Letter</DialogTitle>
          <DialogDescription>A general cover letter based on your resume and target role — review before sending.</DialogDescription>
        </DialogHeader>
        <input
          value={company} onChange={(e) => setCompany(e.target.value)} placeholder="Target company (optional)" data-testid="resume-cover-letter-company-input"
          className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-emerald-400"
        />
        <button onClick={generate} disabled={generating} data-testid="resume-cover-letter-generate-button" className="flex items-center justify-center gap-2 bg-emerald-500 hover:bg-emerald-400 disabled:opacity-60 text-white text-sm font-semibold px-5 py-2.5 rounded-lg transition-colors">
          {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />} Generate
        </button>
        {letter && (
          <div data-testid="resume-cover-letter-result">
            <pre className="text-xs text-slate-300 whitespace-pre-wrap bg-white/5 rounded-lg p-3 max-h-64 overflow-y-auto">{letter}</pre>
            <button onClick={copyLetter} data-testid="resume-cover-letter-copy-button" className="mt-3 w-full flex items-center justify-center gap-2 bg-white/10 hover:bg-white/15 text-white text-sm font-semibold px-5 py-2.5 rounded-lg transition-colors">
              <Copy className="w-4 h-4" /> Copy Cover Letter
            </button>
            <button onClick={downloadZipBundle} disabled={downloadingZip} data-testid="resume-cover-letter-download-zip-button" className="mt-2 w-full flex items-center justify-center gap-2 bg-emerald-500 hover:bg-emerald-400 disabled:opacity-60 text-white text-sm font-semibold px-5 py-2.5 rounded-lg transition-colors">
              {downloadingZip ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} Download Resume + Cover Letter (ZIP)
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
